import type { ModuleFeature, Permission } from "@cafe/shared";
import type { LucideIcon } from "lucide-react";
import { ArrowRightLeft, BookOpenCheck, Bell, BookOpen, Bike, Boxes, Factory, Scissors, Send, CalendarHeart, Gift, Globe, Package, LifeBuoy, MessageSquareText, ShieldAlert, Target, Timer, Trophy, Banknote, BriefcaseBusiness, Clock, Coins, Contact, HandCoins as HandCoinsIcon, RotateCcw, ScrollText, UserCog, UserRound, UsersRound } from "lucide-react";
import { Calculator, ChartPie, ClipboardCheck, ClipboardPen, HandCoins, PackageMinus, PackagePlus, Carrot, ChefHat, ClipboardList, CookingPot, CreditCard, LayoutGrid, MonitorSmartphone, ReceiptText, ShieldCheck, Store, UtensilsCrossed, Users, Warehouse, Building2, CalendarClock, Wallet, Truck, Undo2 } from "lucide-react";
import chartLine from "@/assets/icons/line-chart.svg";
import chartSolid from "@/assets/icons/solid-chart.svg";
import receiptLine from "@/assets/icons/line-receipt.svg";
import receiptSolid from "@/assets/icons/solid-receipt.svg";
import locationLine from "@/assets/icons/line-location-alt.svg";
import locationSolid from "@/assets/icons/solid-location-alt.svg";
import calendarLine from "@/assets/icons/line-calendar.svg";
import packageLine from "@/assets/icons/line-package-2.svg";
import packageSolid from "@/assets/icons/solid-package-2.svg";
import userGroupLine from "@/assets/icons/line-user-group.svg";
import settingsLine from "@/assets/icons/line-settings-02.svg";
import projectorSolid from "@/assets/icons/solid-projector-screen-chart.svg";
import taskList from "@/assets/icons/line-task-list.svg";
import type { ru } from "@/shared/i18n/ru";

/** Ключ i18n раздела/пункта навигации. */
export type NavLabel = `nav.${keyof typeof ru.nav}`;

export type NavItem = {
  path: string;
  label: NavLabel;
  /** Иконка из набора Figma (src) или lucide. */
  icon: string | LucideIcon;
  permission?: Permission;
  /** Достаточно любого из прав (например, писать свои заявки или видеть все) */
  anyPermission?: Permission[];
  feature?: ModuleFeature;
  /** Экран ещё не реализован — показывается заглушка. */
  soon?: boolean;
  /** Полноэкранный режим без оболочки ERP (касса, KDS). */
  fullscreen?: boolean;
  /** Короткая подпись под плиткой на тёмной панели (для LAUNCH). */
  short?: NavLabel;
};

export type NavSection = { id: string; label: NavLabel; icon: { line: string; solid: string }; items: NavItem[] };

/** Навигация ERP: тёмная панель (разделы) → сабменю (пункты). Пункты без права скрываются, без функции тарифа — с замком. */
export const NAV: NavSection[] = [
  {
    id: "analytics",
    label: "nav.analytics",
    icon: { line: chartLine, solid: chartSolid },
    items: [
      { path: "/dashboard", label: "nav.dashboard", icon: projectorSolid, permission: "dashboard.view" },
      { path: "/analytics", label: "nav.insights", icon: ChartPie, permission: "reports.view", feature: "core.analytics_basic" },
      { path: "/reports", label: "nav.reports", icon: taskList, permission: "reports.view", feature: "core.analytics_basic" },
      { path: "/expenses", label: "nav.expenses", icon: HandCoins, permission: "expenses.view", feature: "addon.finance" },
      { path: "/fraud", label: "nav.fraud", icon: ShieldAlert, permission: "fraud.view", feature: "addon.fraud" },
    ],
  },
  {
    id: "sales",
    label: "nav.sales",
    icon: { line: receiptLine, solid: receiptSolid },
    items: [
      { path: "/orders", label: "nav.orders", icon: ReceiptText, permission: "orders.view", feature: "core.pos" },
      { path: "/customers", label: "nav.customers", icon: Contact, permission: "customers.view", feature: "core.customers" },
      { path: "/debts", label: "nav.debts", icon: HandCoinsIcon, permission: "debts.view", feature: "core.debts" },
      { path: "/deliveries", label: "nav.deliveries", icon: Bike, permission: "deliveries.view", feature: "addon.delivery" },
      { path: "/courier", label: "nav.courier", icon: Package, permission: "deliveries.courier", feature: "addon.delivery" },
      { path: "/promotions", label: "nav.promotions", icon: Gift, permission: "promotions.view", feature: "addon.promotions_pro" },
      { path: "/sms", label: "nav.sms", icon: MessageSquareText, permission: "sms.view", feature: "addon.sms" },
    ],
  },
  {
    id: "venue",
    label: "nav.venue",
    icon: { line: locationLine, solid: locationSolid },
    items: [
      { path: "/branches", label: "nav.branches", icon: Store, permission: "branches.view" },
      { path: "/tables", label: "nav.tables", icon: LayoutGrid, permission: "tables.view", feature: "core.tables" },
      { path: "/waiter-zones", label: "nav.waiterZones", icon: UsersRound, permission: "waiter_zones.view", feature: "core.tables" },
      { path: "/kitchen-stations", label: "nav.kitchenStations", icon: ChefHat, permission: "kitchen.view", feature: "core.kds" },
    ],
  },
  {
    id: "shifts",
    label: "nav.shifts",
    icon: { line: calendarLine, solid: calendarLine },
    items: [
      { path: "/cash-shifts", label: "nav.cashShifts", icon: Wallet, permission: "cash_shifts.view", feature: "core.pos" },
      { path: "/reservations", label: "nav.reservations", icon: CalendarClock, permission: "reservations.view", feature: "addon.reservations" },
      { path: "/services", label: "nav.services", icon: Timer, permission: "services.view", feature: "addon.services" },
      { path: "/banquets", label: "nav.banquets", icon: CalendarHeart, permission: "banquets.view", feature: "addon.banquets" },
    ],
  },
  {
    id: "catalog",
    label: "nav.catalog",
    icon: { line: packageLine, solid: packageSolid },
    items: [
      { path: "/menu", label: "nav.menu", icon: UtensilsCrossed, permission: "menu.view", feature: "core.menu" },
      { path: "/menu-packs", label: "nav.menuPacks", icon: BookOpenCheck, permission: "menu.view", feature: "core.menu" },
      { path: "/products", label: "nav.products", icon: Carrot, permission: "recipes.view", feature: "core.recipes" },
      { path: "/costing", label: "nav.costing", icon: Calculator, permission: "costing.view", feature: "core.recipes" },
      { path: "/suppliers", label: "nav.suppliers", icon: Truck, permission: "suppliers.view", feature: "addon.procurement" },
      { path: "/purchases", label: "nav.purchases", icon: PackagePlus, permission: "stock_documents.view", feature: "core.inventory_basic" },
      { path: "/purchase-requests", label: "nav.purchaseRequests", icon: ClipboardPen, anyPermission: ["purchase_requests.create", "purchase_requests.view", "purchase_requests.manage"], feature: "core.inventory_basic" },
      { path: "/supplier-returns", label: "nav.supplierReturns", icon: Undo2, permission: "stock_documents.view", feature: "addon.procurement" },
      { path: "/writeoffs", label: "nav.writeoffs", icon: PackageMinus, permission: "stock_documents.view", feature: "core.inventory_basic" },
      { path: "/inventory", label: "nav.inventory", icon: Warehouse, permission: "stock.view", feature: "core.inventory_basic" },
      { path: "/inventory-counts", label: "nav.inventoryCounts", icon: ClipboardCheck, permission: "inventory_counts.view", feature: "core.inventory_basic" },
      { path: "/warehouses", label: "nav.warehouses", icon: Boxes, permission: "warehouses.view", feature: "addon.inventory_pro" },
      { path: "/transfers", label: "nav.transfers", icon: ArrowRightLeft, permission: "stock_transfers.view", feature: "addon.inventory_pro" },
      { path: "/expiry", label: "nav.expiry", icon: CalendarClock, permission: "stock.view", feature: "addon.inventory_pro" },
      { path: "/stock-requests", label: "nav.stockRequests", icon: Send, permission: "stock_transfers.view", feature: "addon.inventory_pro" },
      { path: "/conversions", label: "nav.conversions", icon: Scissors, permission: "stock_documents.view", feature: "addon.inventory_pro" },
      { path: "/production", label: "nav.production", icon: Factory, permission: "production.view", feature: "addon.production" },
    ],
  },
  {
    id: "staff",
    label: "nav.staff",
    icon: { line: userGroupLine, solid: userGroupLine },
    items: [
      { path: "/staff/users", label: "nav.users", icon: Users, permission: "users.view" },
      { path: "/staff/roles", label: "nav.roles", icon: ShieldCheck, permission: "roles.view" },
      { path: "/staff/employees", label: "nav.employees", icon: UserCog, permission: "employees.view", feature: "core.staff" },
      { path: "/staff/positions", label: "nav.positions", icon: BriefcaseBusiness, permission: "employees.view", feature: "core.staff" },
      { path: "/staff/shifts", label: "nav.workShifts", icon: Clock, permission: "work_shifts.view", feature: "core.staff" },
      { path: "/staff/payroll", label: "nav.payroll", icon: Banknote, permission: "payroll.view", feature: "core.payroll_basic" },
      { path: "/staff/tips", label: "nav.tips", icon: Coins, permission: "tips.view", feature: "core.pos" },
      { path: "/staff/goals", label: "nav.goals", icon: Target, permission: "goals.view", feature: "core.staff" },
      { path: "/me/work", label: "nav.myWork", icon: UserRound },
      { path: "/me/goals", label: "nav.myGoals", icon: Trophy, feature: "core.staff" },
    ],
  },
  {
    id: "settings",
    label: "nav.settings",
    icon: { line: settingsLine, solid: settingsLine },
    items: [
      { path: "/settings/company", label: "nav.company", icon: Building2, permission: "settings.view" },
      { path: "/settings/devices", label: "nav.devices", icon: MonitorSmartphone, permission: "devices.view" },
      { path: "/settings/subscription", label: "nav.subscription", icon: ClipboardList, permission: "subscription.view" },
      { path: "/settings/site", label: "nav.site", icon: Globe, permission: "settings.view", feature: "core.qr_menu" },
      { path: "/settings/audit", label: "nav.audit", icon: ScrollText, permission: "audit.view" },
      { path: "/settings/report-reset", label: "nav.reportReset", icon: RotateCcw, permission: "report_resets.view", feature: "core.analytics_basic" },
      { path: "/settings/notifications", label: "nav.notifications", icon: Bell },
      { path: "/help", label: "nav.help", icon: BookOpen },
      { path: "/settings/security", label: "nav.security", icon: ShieldCheck },
      { path: "/support", label: "nav.support", icon: LifeBuoy, permission: "support.view" },
    ],
  },
];

/** Рабочие экраны (касса, KDS): отдельные плитки основного цвета внизу тёмной панели, вне разделов. */
export const LAUNCH: NavItem[] = [
  { path: "/pos", label: "nav.pos", short: "nav.posShort", icon: CreditCard, permission: "pos.view", feature: "core.pos", fullscreen: true },
  { path: "/kitchen", label: "nav.kitchen", short: "nav.kitchen", icon: CookingPot, permission: "kitchen.view", feature: "core.kds", fullscreen: true },
];

/** Все страницы навигации — для маршрутов. */
export const NAV_ITEMS: NavItem[] = [...LAUNCH, ...NAV.flatMap((s) => s.items)];

export const findNav = (pathname: string) => {
  for (const s of NAV) for (const i of s.items) if (pathname === i.path || pathname.startsWith(`${i.path}/`)) return { section: s, item: i };
  return undefined;
};
